import { Tooltip, tooltipClasses } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import FileFormatMapper from "../FileFormatMapper";
import "./index.css";
import CommonToast from "../CommonToast";
import ICONS from "../../../assets/Icons";
import { GlobalConfiguration } from "../../../utils/configurationProvider";

/**
 * @param {Object} props
 * @param {boolean} props.isDisabled
 * @param {object} props.files - state for quantity
 * @param {object} props.setFiles - setter for quantity
 * @param {string} props.id - unique id for quantity
 * @param {boolean} props.readOnly
 * @param {("file" | "image" | "all" | "both" | "custom")} props.type
 * Value =
 * - `file`: xls, xlsx, csv, pdf files allowed
 * - `image`: jpg, png, gif, etc. allowed
 * - `both`: `file` + `image` types allowed
 * - `all`: no restriction on what files are allowed
 * - `custom`: Please specify file extension list in the `customExtensions` prop.
 * @param {String[]} props.customExtensions Specify list of allowed extensions only when `type` = `custom`
 * Specify list like this `[".svg", ".exe"]` Please don't forget the dots.
 * @returns {JSX.Element}
 */
const CommonFileUpload = ({
  files,
  setFiles,
  isDisabled,
  type,
  id,
  readOnly,
  customExtensions,
}) => {
  const dragCounter = useRef(GlobalConfiguration.lengthCheckZero);
  const [dragActive, setDragActive] = useState(false);
  const [showImages, setShowImages] = useState("");
  const [attemptedErrorRecovery, setAttemptedErrorRecovery] = useState(false);
  const file_extensions = [".xls", ".xlsx", ".csv", ".pdf"];
  const image_extensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".tiff",
    ".webp",
  ];
  // todo
  let acceptExtension = [];

  // const acceptExtension = ["*"];

  const formatExtensionList = (list) => {
    const formatter = new Intl.ListFormat("en", {
      style: "long",
      type: "disjunction",
    });
    return formatter.format(list);
  };

  switch (type) {
    case "both":
    case "file":
      acceptExtension = [...acceptExtension, ...file_extensions];
      if (type === "file") break;
    case "image":
      acceptExtension = [...acceptExtension, ...image_extensions];
      break;
    case "custom":
      if (!customExtensions)
        throw new Error(
          "type = custom without custom extension list. Behaviour is undefined.",
        );
    case "all":
    default:
      acceptExtension = ["*"];
  }

  if (customExtensions) {
    if (type !== "custom")
      console.warn(
        "Custom extensions provided with type !== 'custom'. Expect strange behaviour.",
      );
    acceptExtension = customExtensions;
  }
  const isAllFormatsAllowed = acceptExtension[0] === "*";

  useEffect(() => {
    const f = Array.isArray(files) ? files : [files];

    if (f.length && f[0] instanceof Blob) {
      setFiles(f[0]);

      const reader = new FileReader();
      reader.onload = () => {
        setShowImages(reader.result);
      };
      reader.readAsDataURL(f[0]);
    } else if (files?.uploadURL) {
      setShowImages(files?.uploadURL);
    } else {
      setShowImages("doesnotexist.jpg");
    }
  }, []);

  useEffect(() => {
    if (!Array.isArray(files) || files?.length) {
    } else {
      setShowImages(brower_file);
    }
  }, [files]);

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    const formatted = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
    return `${formatted} ${sizes[i]}`;
  }

  const handleDragOver = (event) => {
    event.preventDefault();
    if (readOnly) return;
    setDragActive(true);
  };

  const handleInputChange = (e, data_type) => {
    if (data_type === "numeric" && e.target.value.length > 15) {
      return;
    }
    if (e.preventDefault) e.preventDefault();
  };
  const brower_file = ICONS?.BROWSE_FILE;

  const CircleCancel = ICONS?.ABANDONED_WHITE;
  return (
    <article
      className={`flex global-border-dash gap-x-gap-md items-center  py-padding-md ${isDisabled ? "opacity-light-opacity pointer-events-none cursor-not-allowed" : ""} ${
        dragActive ? "upload-file-hover z-z-index-base " : "bg-transparent"
      }`}
      onDragEnter={(e) => {
        e.preventDefault();
        if (readOnly) return;

        dragCounter.current++;
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        if (readOnly) return;

        dragCounter.current--;
        if (dragCounter.current === 0) {
          setDragActive(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (readOnly) return;

        dragCounter.current = 0;
        setDragActive(false);

        const selectedFile = e.dataTransfer.files[0];
        if (!selectedFile) return;

        const allowedExtensions = acceptExtension;
        const fileName = selectedFile.name.toLowerCase();
        const hasValidExtension = allowedExtensions.every((e) => e === "*")
          ? true
          : allowedExtensions.some((ext) => fileName.endsWith(ext));

        if (!hasValidExtension) {
          CommonToast({
            text: "The uploaded file format is not supported",
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
          return;
        }
        setFiles(selectedFile);
        const reader = new FileReader();
        reader.onload = () => {
          setShowImages(reader.result);
        };
        reader.readAsDataURL(selectedFile);

        handleInputChange({
          target: {
            name: "upload file",
            value: selectedFile,
            data_typeChan: "upload",
          },
        });
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      style={{
        borderRadius: "var(--border-radius-md)",
        padding: "var(--margin-md)",
        paddingRight: "var(--bene-txn-dropdown-height)",
        margin:
          "var(margin-xs) var(margin-base) var(margin-base) var(margin-base) ",
        position: "relative",
        minHeight: "var(--timeline-height-md)",
      }}
    >
      <img
        src={showImages}
        className="file-upload-image"
        onError={function (e) {
          if (attemptedErrorRecovery) {
            if (e.target.src !== ICONS?.DEFAULT_FILE)
              setShowImages(ICONS?.DEFAULT_FILE);
          } else {
            setAttemptedErrorRecovery(true);
            let f = FileFormatMapper(
              files?.name?.split(".").pop().toLowerCase(),
              true,
            );
            setShowImages(f);
            return false;
          }
        }}
        alt="file"
      />

      {files?.length === 0 ? (
        <>
          <div
            className={`flex flex-col flex-1 ${
              dragActive ? "justify-center items-start text-center h-full" : ""
            }`}
          >
            <p className="file-upload-header">
              {dragActive ? (
                "Drop your file here"
              ) : (
                <>
                  Drag and Drop your file here OR
                  <label
                    style={{ lineHeight: "var(--font-sm)" }}
                    htmlFor={`file-input-${id}`}
                    className="text-emphasis underline cursor-pointer text-font-sm font-semibold ml-margin-xs hover:opacity-dark-opacity"
                  >
                    Browse file
                  </label>
                </>
              )}
            </p>
            {!dragActive && (
              <>
                {(type !== "all" || customExtensions) && (
                  <p className="font-sans font-normal text-font-xs text-text-primary">
                    {isAllFormatsAllowed ? (
                      "Upload data in preferred format"
                    ) : (
                      <>
                        Upload your data through a&nbsp;
                        {formatExtensionList(acceptExtension)} file
                      </>
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <div className="file-upload-main-div">
          <div className="file-upload-sub-div">
            <p className="file-upload-header ">{files?.name}</p>
            <p className="font-sans font-normal text-font-xs text-text-primary">
              Uploaded file size : {formatBytes(files?.size)}
            </p>
          </div>

          {!readOnly && (
            <Tooltip
              componentsProps={{
                popper: {
                  sx: {
                    [`& .${tooltipClasses.tooltip}`]: {
                      backgroundColor: (theme) => theme.palette.common.black,
                    },
                    [`& .${tooltipClasses.arrow}`]: {
                      color: (theme) => theme.palette.common.black,
                    },
                  },
                },
              }}
              arrow
              PopperProps={{
                style: {
                  maxWidth: "none",
                },
              }}
              title="Remove file "
              placement="bottom-start"
            >
              <img
                src={CircleCancel}
                className="h-icon-md w-icon-md cursor-pointer hover:opacity-light-opacity "
                alt="cancel"
                onClick={() => {
                  setAttemptedErrorRecovery(false);
                  setFiles([]);
                  setShowImages("");
                }}
              />
            </Tooltip>
          )}
        </div>
      )}
      <input
        type="file"
        accept={acceptExtension.toString()}
        onClick={(event) => {
          event.target.value = null; // reset to allow re-uploading same file
        }}
        onChange={(e) => {
          e.preventDefault();
          const selectedFile = e.target.files[0];
          if (!selectedFile) return;

          const allowedExtensions = acceptExtension;
          const fileName = selectedFile.name.toLowerCase();

          // if the array is just "*" then all file extensions are valid
          const hasValidExtension = allowedExtensions.every((e) => e === "*")
            ? true
            : allowedExtensions.some((ext) => fileName.endsWith(ext));

          if (!hasValidExtension) {
            CommonToast({
              text: "The uploaded file format is not supported",
              icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
              variant: "var(--error)",
              buttonNeeded: false,
            });
            return;
          }
          setFiles(selectedFile);
          const reader = new FileReader();
          reader.onload = () => {
            setShowImages(reader.result);
          };
          reader.readAsDataURL(selectedFile);

          handleInputChange({
            target: {
              name: "upload file",
              value: selectedFile,
              data_typeChan: "upload",
            },
          });
        }}
        style={{ display: "none" }}
        id={`file-input-${id}`}
      />
    </article>
  );
};

export default CommonFileUpload;
